import { ChangeEvent, FC, FormEvent, useRef, useState } from 'react'
import classNames from 'classnames'
import Modal from '@common/Modal'
import mclasses from '@pages/Admin/CRM/styles/modal.module.css'
import sharedClasses from '@modals/index.module.css'
import Button from '@UI/New/Button'
import { InputWhite } from '@UI/New/InputWhite'
import { IcoRemove } from '@/utils/icons'
import { crmLeadContractApi } from '@/api'
import { isError } from '@/helpers/checkQuery'
import { useAddAlert } from '@/hooks/useAddAlert'
import addContractClasses from '@pages/Admin/AllUsers/modals/AddContractModal/index.module.css'

type LeadAddContractModalProps = {
  open: boolean
  leadId: number
  onClose: () => void
  onSuccess: () => void | Promise<void>
}

const LeadAddContractModal: FC<LeadAddContractModalProps> = ({
  open,
  leadId,
  onClose,
  onSuccess,
}) => {
  const addAlert = useAddAlert()
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [name, setName] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const reset = () => {
    setName('')
    setFile(null)
    setIsSubmitting(false)
  }

  const handleClose = () => {
    if (isSubmitting) return
    reset()
    onClose()
  }

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0]
    if (!selectedFile) return
    setFile(selectedFile)
    event.currentTarget.value = ''
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!name.trim() || !file || !leadId) return

    const formData = new FormData()
    formData.append('file', file)
    formData.append('name', name.trim())
    formData.append('lead_id', String(leadId))

    setIsSubmitting(true)
    try {
      const res = await crmLeadContractApi.upload(formData)
      if (isError(res)) throw new Error()
      await onSuccess()
      addAlert({
        type: 'success',
        title: 'Успешно',
        text: 'Договор загружен',
      })
      reset()
      onClose()
    } catch {
      addAlert({
        type: 'error',
        title: 'Ошибка',
        text: 'Не удалось загрузить договор',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!open) return null

  const isSubmitDisabled = !name.trim() || !file || isSubmitting

  return (
    <Modal
      active={open}
      setActive={handleClose}
      className={addContractClasses.modalWrapper as any}
      styleContent={{ minWidth: 'auto', padding: 0 }}
    >
      <div className={sharedClasses.modalContainer}>
        <div className={sharedClasses.modalHeader}>
          <div className={sharedClasses.modalTitleContainer}>
            <span className={sharedClasses.modalHeaderBigTitle}>
              Добавить договор
            </span>
          </div>
          <div className={mclasses.closeIcon} onClick={handleClose}>
            <IcoRemove />
          </div>
        </div>

        <form
          onSubmit={(e) => void handleSubmit(e)}
          className={classNames(
            sharedClasses.contentWrapper,
            addContractClasses.form,
          )}
        >
          <InputWhite
            title="Название договора"
            value={name}
            onChange={setName}
            placeholder="Введите название файла"
            className={addContractClasses.input}
          />

          <div className={addContractClasses.field}>
            <span className={addContractClasses.fieldTitle}>Файл договора</span>
            <input
              ref={fileInputRef}
              type="file"
              className={addContractClasses.hiddenInput}
              onChange={handleFileChange}
            />
            <Button
              type="button"
              color="gray"
              disabled={isSubmitting}
              onClick={() => fileInputRef.current?.click()}
            >
              {file ? 'Выбрать другой файл' : 'Загрузить файл'}
            </Button>
            {file ? (
              <ul className={addContractClasses.filesList}>
                <li className={addContractClasses.fileItem}>{file.name}</li>
              </ul>
            ) : null}
          </div>

          <div className={addContractClasses.footer}>
            <Button
              type="submit"
              color="primary"
              disabled={isSubmitDisabled}
              isLoading={isSubmitting}
              className={addContractClasses.submitButton}
            >
              Сохранить
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  )
}

export default LeadAddContractModal
